/**
 * CoConnect — ProfileButton
 *
 * Universal profile + change-password sheet accessible from any portal.
 * Works for every role (admin, field_agent, farmer, driver, finance).
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, IconButton, Alert, Spinner } from './ui';
import OfflineStatusPanel from './OfflineStatusPanel';

interface Profile {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  role: string;
  bio: string | null;
  profile_pic_url: string | null;
  must_change_password: boolean;
  last_password_change_at: string | null;
  last_login: string | null;
  village_name?: string | null;
  assigned_villages?: { id: string; name: string; region: string | null }[];
  driver_truck_registration?: string | null;
  linked_farmer_id?: string | null;
}

const ProfileButton: React.FC<{ accent?: 'admin' | 'agrarian' | 'cargo' | 'terrain' | 'analytics' }> = ({ accent = 'admin' }) => {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [profilePicUrl, setProfilePicUrl] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const isDriverProfile = accent === 'cargo';

  useEffect(() => {
    if (!open) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.classList.add('profile-sheet-open');

    const overlay = overlayRef.current;
    const focusableSelector = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';
    const firstFocusable = overlay?.querySelector<HTMLElement>(focusableSelector);
    requestAnimationFrame(() => firstFocusable?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !overlay) return;
      const focusables = Array.from(overlay.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => !el.hasAttribute('disabled'));
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.classList.remove('profile-sheet-open');
      requestAnimationFrame(() => triggerElementRef.current?.focus());
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    apiClient.get('/users/me').then(res => {
      const p = res.data.profile;
      setProfile(p);
      setName(p.name || ''); setPhone(p.phone || ''); setBio(p.bio || ''); setProfilePicUrl(p.profile_pic_url || '');
    }).catch(err => setError(err.response?.data?.error || 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSaveProfile = async () => {
    setError(null); setSuccess(null); setSavingProfile(true);
    try {
      await apiClient.put('/users/me', { name, phone, bio, profile_pic_url: profilePicUrl });
      setSuccess('Profile updated.');
      refreshUser();
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally { setSavingProfile(false); }
  };

  const handleChangePassword = async () => {
    setError(null); setSuccess(null);
    if (newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('New passwords do not match'); return; }
    setSavingPassword(true);
    try {
      await apiClient.put('/users/me/password', { current_password: currentPassword, new_password: newPassword });
      setSuccess('Password changed successfully.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      refreshUser();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally { setSavingPassword(false); }
  };

  return (
    <>
      <IconButton label="My profile" onClick={(event) => { triggerElementRef.current = event.currentTarget as HTMLElement; setOpen(true); }}>
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
        </svg>
      </IconButton>

      {open && createPortal(
        <div
          ref={overlayRef}
          className={`profile-sheet-overlay profile-sheet-overlay--${accent}`}
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <section
            className={`profile-sheet-panel ${isDriverProfile ? 'profile-sheet-panel--driver' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-sheet-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="profile-sheet-header">
              <div className="min-w-0">
                {!isDriverProfile && <p className="profile-sheet-eyebrow">Account</p>}
                <h3 id="profile-sheet-title" className="profile-sheet-title">My profile</h3>
                {isDriverProfile && <p className="profile-sheet-brand">COCONNECT</p>}
              </div>
              <button type="button" className="profile-sheet-close" aria-label="Close profile" onClick={() => setOpen(false)}>
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="profile-sheet-body">
              {loading && <div className="flex justify-center py-6"><Spinner /></div>}
              {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
              {success && <Alert tone="success">{success}</Alert>}

              {!loading && profile && (
                <>
                  <div className={`profile-identity ${isDriverProfile ? 'profile-identity--driver' : ''}`}>
                    <Avatar name={profile.name || 'U'} size="xl" />
                    <div className="profile-identity-copy">
                      <h2 className="profile-identity-name">{profile.name}</h2>
                      <p className="profile-identity-email">{profile.email}</p>
                      <div className="profile-identity-meta">
                        <span className={isDriverProfile ? 'profile-driver-role' : 'pill-info'}>{profile.role}</span>
                        {profile.must_change_password && <span className="pill-warning">Must change password</span>}
                        {profile.last_password_change_at && (
                          <span className="profile-password-date">Password changed {new Date(profile.last_password_change_at).toLocaleDateString()}</span>
                        )}
                      </div>
                      {isDriverProfile && (
                        <div className="profile-assigned-vehicle">
                          <span>Assigned vehicle</span>
                          <strong>{profile.driver_truck_registration || 'No truck assigned'}</strong>
                        </div>
                      )}
                    </div>
                  </div>

                  {profile.role === 'field_agent' && profile.assigned_villages && profile.assigned_villages.length > 0 && (
                    <div className="p-4 rounded-lg surface-inset">
                      <p className="section-eyebrow mb-2">Your assigned villages</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.assigned_villages.map(v => <span key={v.id} className="pill-info">{v.name}{v.region ? ` · ${v.region}` : ''}</span>)}
                      </div>
                    </div>
                  )}
                  {!isDriverProfile && profile.role === 'driver' && profile.driver_truck_registration && (
                    <div className="p-4 rounded-lg surface-inset">
                      <p className="section-eyebrow mb-1">Assigned truck</p>
                      <p className="text-sm font-semibold text-cargo-700 dark:text-cargo-500">{profile.driver_truck_registration}</p>
                    </div>
                  )}
                  {profile.role === 'farmer' && profile.linked_farmer_id && (
                    <div className="p-4 rounded-lg surface-inset">
                      <p className="section-eyebrow mb-1">Farmer profile</p>
                      <p className="text-sm">Linked to farmer record · use the Farmer Portal for farm/batch operations.</p>
                    </div>
                  )}

                  <OfflineStatusPanel />

                  <div className="profile-section">
                    <div className="profile-section-heading">
                      <p className="profile-section-eyebrow">Personal details</p>
                      <p className="profile-section-helper">Keep your Driver contact details current.</p>
                    </div>
                    <div className="profile-form-grid">
                      <div><label className="label" htmlFor="profile-name">Name</label><input id="profile-name" className="input" value={name} onChange={e => setName(e.target.value)} autoComplete="name" /></div>
                      <div><label className="label" htmlFor="profile-phone">Phone</label><input id="profile-phone" className="input" type="tel" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" /></div>
                      <div className="profile-field-wide"><label className="label" htmlFor="profile-bio">Bio</label><textarea id="profile-bio" className="input profile-bio" rows={3} value={bio} onChange={e => setBio(e.target.value)} /></div>
                      {isDriverProfile ? (
                        <details className="profile-advanced profile-field-wide">
                          <summary>Profile picture URL</summary>
                          <div className="profile-advanced-body"><label className="label" htmlFor="profile-picture-url">Image URL</label><input id="profile-picture-url" className="input" value={profilePicUrl} onChange={e => setProfilePicUrl(e.target.value)} placeholder="https://…" inputMode="url" /></div>
                        </details>
                      ) : (
                        <div className="profile-field-wide"><label className="label" htmlFor="profile-picture-url">Profile picture URL</label><input id="profile-picture-url" className="input" value={profilePicUrl} onChange={e => setProfilePicUrl(e.target.value)} placeholder="https://…" /></div>
                      )}
                    </div>
                    <button onClick={handleSaveProfile} disabled={savingProfile} className={`profile-primary-action btn-accent ${isDriverProfile ? 'brand-surface-cargo' : 'brand-surface-admin'}`}>
                      {savingProfile ? <Spinner size="sm" /> : null} Save profile
                    </button>
                  </div>

                  <div className="profile-section profile-security">
                    <button type="button" className="profile-security-trigger" aria-expanded={securityOpen} onClick={() => setSecurityOpen(v => !v)}>
                      <span><strong>Security</strong><small>Change your account password</small></span>
                      <svg viewBox="0 0 24 24" className={securityOpen ? 'is-open' : ''} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                    </button>
                    {(securityOpen || !isDriverProfile) && (
                      <div className="profile-security-body">
                        <div className="profile-password-grid">
                          <div><label className="label" htmlFor="profile-current-password">Current password</label><input id="profile-current-password" className="input" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} autoComplete="current-password" /></div>
                          <div><label className="label" htmlFor="profile-new-password">New password</label><input id="profile-new-password" className="input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" placeholder="Min 8 chars" /></div>
                          <div><label className="label" htmlFor="profile-confirm-password">Confirm password</label><input id="profile-confirm-password" className="input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" /></div>
                        </div>
                        <button onClick={handleChangePassword} disabled={savingPassword} className="profile-secondary-action btn-secondary">
                          {savingPassword ? <Spinner size="sm" /> : null} Change password
                        </button>
                      </div>
                    )}
                  </div>

                  {profile?.role === 'farmer' && <NotificationPreferencesSection />}
                </>
              )}
            </div>
          </section>
        </div>,
        document.body
      )}
    </>
  );
};

/* ─── Notification Preferences (SMS opt-in) — Phase 29.6 ─── */
const NotificationPreferencesSection: React.FC = () => {
  const [prefs, setPrefs] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const refresh = () => {
    setLoading(true);
    apiClient.get('/sms/preferences').then(res => {
      setPrefs(res.data.preferences);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      await apiClient.post('/sms/preferences', {
        sms_enabled: prefs.sms_enabled,
        sms_phone: prefs.sms_phone,
        preferred_language: prefs.preferred_language,
        notification_time: prefs.notification_time,
        min_alert_severity: prefs.min_alert_severity,
      });
      setSuccess('Preferences saved');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="mt-4"><Spinner size="sm" /></div>;

  return (
    <div className="mt-4 p-4 rounded-lg surface-inset">
      <p className="section-eyebrow mb-2">Notification preferences (SMS)</p>
      {error && <div className="mb-2"><Alert tone="danger" onClose={() => setError(null)}>{error}</Alert></div>}
      {success && <div className="mb-2"><Alert tone="success">{success}</Alert></div>}

      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={prefs.sms_enabled || false}
            onChange={e => setPrefs({ ...prefs, sms_enabled: e.target.checked })}
          />
          <span>Enable daily SMS advisory</span>
        </label>

        {prefs.sms_enabled && (
          <>
            <div>
              <label className="label">SMS phone (defaults to your registered phone)</label>
              <input
                className="input"
                value={prefs.sms_phone || ''}
                onChange={e => setPrefs({ ...prefs, sms_phone: e.target.value })}
                placeholder="+2376XXXXXXXX"
              />
            </div>
            <div>
              <label className="label">Preferred language</label>
              <select
                className="input"
                value={prefs.preferred_language || 'en'}
                onChange={e => setPrefs({ ...prefs, preferred_language: e.target.value })}
              >
                <option value="en">English</option>
                <option value="fr">French</option>
                <option value="pcm">Cameroonian Pidgin English</option>
                <option value="oroko">Oroko</option>
              </select>
            </div>
            <div>
              <label className="label">Daily SMS time</label>
              <input
                className="input"
                type="time"
                value={prefs.notification_time || '06:00'}
                onChange={e => setPrefs({ ...prefs, notification_time: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Minimum alert severity to receive via SMS</label>
              <select
                className="input"
                value={prefs.min_alert_severity || 'warning'}
                onChange={e => setPrefs({ ...prefs, min_alert_severity: e.target.value })}
              >
                <option value="info">All alerts (info + warning + critical)</option>
                <option value="warning">Warning + critical only</option>
                <option value="critical">Critical only</option>
              </select>
            </div>
          </>
        )}

        <button onClick={handleSave} disabled={saving} className="btn-secondary text-sm">
          {saving ? <Spinner size="sm" /> : null} Save preferences
        </button>
      </div>
    </div>
  );
};

export default ProfileButton;
